// Brand Ambassador portfolios (BA P1.6a) through the gateway: employee-service
// (/api/v1/employees/*: P1.1 editor + review, P1.7 images, P1.8 marks and
// preview) and commerce-service (/api/v1/business-services/*: P1.3 catalogue).
// Presentation only: the services enforce the flag + entitlement, the reviewer
// fence and no self-review; a hidden screen is never the check.
import { gatewayApi } from '@/lib/hrApi';

const ME = '/api/v1/employees/me/ba-profile';
const REVIEW = '/api/v1/employees/ba-profiles';
const CATALOGUE = '/api/v1/business-services/ba-profiles';

export interface BaExperience { title: string; detail?: string; year?: number }

/** The BA-provided fields — and only these (no contact details, no rates). */
export interface BaContent {
  display_name?: string | null;
  bio?: string | null;
  skills?: string[] | null;
  languages?: string[] | null;
  service_areas?: string[] | null;
  experience?: BaExperience[] | null;
}

/** BA-provided vs company-verified, per field ("experience": one per item). */
export type VerificationValue = 'ba_provided' | 'fern_verified';
export interface FieldVerification {
  display_name?: VerificationValue;
  bio?: VerificationValue;
  skills?: VerificationValue;
  languages?: VerificationValue;
  service_areas?: VerificationValue;
  experience?: VerificationValue[];
  /** Day each verified field was checked (P1.8; never who). */
  verifiedAt?: { skills?: string | null; languages?: string | null; service_areas?: string | null; experience?: (string | null)[] };
  publication?: string;
}

/** One reviewer mark on a revision (P1.8 `verification_marks`). Reviewer-only:
 *  never in a client view. P1.8 sends the reviewer's user id, not a name. */
export interface VerificationMark {
  field: string;
  item: number | null;
  value_sha256?: string;
  verified_by: string | null;
  verified_by_name?: string | null;
  verified_at: string;
  carried?: boolean;
}

/** THE client view (P1.8 client_view = P1.3 projection). The only shape any
 *  "what clients see" surface renders — never employee data merged client-side. */
export interface BaClientView {
  profileId: string;
  revisionNo: number;
  publicationVersion: number;
  publishedAt: string | null;
  content: BaContent;
  mediaIds: string[];
  verification?: FieldVerification;
}

/** P1.8 preview sources: "revision" = a revision that is NOT live (reviewer only). */
export type BaPreviewSource = 'draft' | 'published' | 'revision';

export interface BaPreview {
  source: BaPreviewSource;
  revisionNo: number | null;
  publishedAt: string | null;
  view: BaClientView;
}

export type BaDraftStatus = 'draft' | 'submitted' | 'changes_requested' | 'approved';

export interface BaRevision {
  id: string;
  revision_no: number;
  status: string;
  content: BaContent;
  media_ids: string[];
  submitted_at: string | null;
  consent_version: string | null;
  reviewed_at: string | null;
  decision_reason?: string | null;
  /** P1.8: the per-field LABELS (an object, every view). */
  verification?: FieldVerification;
  /** P1.8: the marks behind them — who and when (reviewer views only). */
  verification_marks?: VerificationMark[];
}

export interface BaPublication {
  status: 'published' | 'unpublished';
  version: number;
  revision_id?: string | null;
  published_at?: string | null;
  unpublished_at?: string | null;
}

export interface MyBaProfile {
  id: string | null;
  draft: BaContent;
  draft_status: BaDraftStatus;
  version: number;
  submitted_revision: BaRevision | null;
  published_revision: BaRevision | null;
  publication: BaPublication;
  last_decision: { decision: string; reason: string | null; revision_no: number; decided_at: string | null } | null;
  /** P1.8: the draft's marks — a field shows verified only while its value is
   *  unchanged since the reviewer checked it. Absent → everything BA-provided. */
  draft_verification?: FieldVerification;
}

export interface BaRevisionSummary { id: string; revision_no: number; status: string; submitted_at: string | null }

export interface ReviewerBaProfile {
  id: string;
  employee_id: string;
  employee_name: string | null;
  managing_entity_id: string | null;
  draft_status: BaDraftStatus;
  version: number;
  submitted_revision: BaRevision | null;
  published_revision: BaRevision | null;
  publication: BaPublication;
  revisions: BaRevisionSummary[];
}

export type BaRosterStatus = 'submitted' | 'changes_requested' | 'approved' | 'draft' | 'published' | 'unpublished';

// ── the BA's own profile ────────────────────────────────────────────────────

export const getMyBaProfile = async (): Promise<MyBaProfile> =>
  (await gatewayApi.get<MyBaProfile>(ME)).data;

/** Merge PATCH (owner AC1): send ONLY the fields the BA changed. A field left out
 *  keeps its value server-side (P1.7) — images and portrait are never sent by a
 *  text edit, so a text save can never erase them. */
export const saveMyBaDraft = async (changes: Partial<BaContent>, expectedVersion: number): Promise<MyBaProfile> =>
  (await gatewayApi.patch<MyBaProfile>(ME, { ...changes, expected_version: expectedVersion })).data;

/** Preview as client (P1.8): the client view of the draft or the live version. */
export const getMyBaPreview = async (source: 'draft' | 'published'): Promise<BaPreview> =>
  (await gatewayApi.get<BaPreview>(`${ME}/preview`, { params: { source } })).data;

export const submitMyBaProfile = async (consentVersion: string, expectedVersion: number): Promise<MyBaProfile> =>
  (await gatewayApi.post<MyBaProfile>(`${ME}/submit`, {
    consent: true, consent_version: consentVersion, expected_version: expectedVersion,
  })).data;

export const withdrawMyBaSubmission = async (): Promise<MyBaProfile> =>
  (await gatewayApi.post<MyBaProfile>(`${ME}/withdraw`)).data;

/** "Remove me from the catalogue" — withdraws publication consent. Never gated. */
export const unpublishMyBaProfile = async (): Promise<MyBaProfile> =>
  (await gatewayApi.post<MyBaProfile>(`${ME}/unpublish`)).data;

// ── reviewers (ba:profile:review) ───────────────────────────────────────────

export const listBaProfiles = async (status?: BaRosterStatus): Promise<ReviewerBaProfile[]> =>
  (await gatewayApi.get<{ profiles: ReviewerBaProfile[] }>(REVIEW, { params: status ? { status } : undefined }))
    .data.profiles;

export const getBaProfile = async (id: string): Promise<ReviewerBaProfile> =>
  (await gatewayApi.get<ReviewerBaProfile>(`${REVIEW}/${id}`)).data;

/** What the reviewer checked (P1.8): field names (never bio/display_name) and experience item indexes. */
export interface VerifiedChecks { fields: string[]; experience: number[] }

/** `verified` given — even EMPTY — is the complete set: an empty one removes every
 *  carried mark. Left out (undefined), P1.8 keeps the carried marks as they are. */
export const approveBaRevision = async (id: string, revisionId: string, verified?: VerifiedChecks): Promise<ReviewerBaProfile> =>
  (await gatewayApi.post<ReviewerBaProfile>(`${REVIEW}/${id}/revisions/${revisionId}/approve`,
    verified !== undefined ? { verified } : {})).data;

/** Reviewer preview as client (P1.8): a revision id, or 'published'. */
export const getBaPreview = async (id: string, revision: string): Promise<BaPreview> =>
  (await gatewayApi.get<BaPreview>(`${REVIEW}/${id}/preview`, { params: { revision } })).data;

export const requestBaChanges = async (id: string, revisionId: string, reason: string): Promise<ReviewerBaProfile> =>
  (await gatewayApi.post<ReviewerBaProfile>(`${REVIEW}/${id}/revisions/${revisionId}/request-changes`, { reason })).data;

export const publishBaProfile = async (id: string, revisionId?: string): Promise<ReviewerBaProfile> =>
  (await gatewayApi.post<ReviewerBaProfile>(`${REVIEW}/${id}/publish`, revisionId ? { revision_id: revisionId } : {})).data;

export const unpublishBaProfile = async (id: string, reason: string): Promise<ReviewerBaProfile> =>
  (await gatewayApi.post<ReviewerBaProfile>(`${REVIEW}/${id}/unpublish`, { reason })).data;

/** "Approve & publish" is TWO calls: approve the revision, then publish it. If
 *  the publish fails the approval stands, and the caller is told which step
 *  failed (`step`). */
export async function approveAndPublishBa(id: string, revisionId: string, verified?: VerifiedChecks): Promise<ReviewerBaProfile> {
  await approveBaRevision(id, revisionId, verified);
  try {
    return await publishBaProfile(id, revisionId);
  } catch (e) {
    (e as any).step = 'publish';
    throw e;
  }
}

// ── "What clients see" (P1.3 provider view, commerce) ───────────────────────
// The provider view of the caller's own company. The buyer view (?provider=)
// is not used here: in the pilot clients don't sign in (decision D).

export interface BaCatalogueFilters { skill?: string; language?: string; service_area?: string }

export const listBaCatalogue = async (filters: BaCatalogueFilters = {}, cursor?: string):
  Promise<{ profiles: BaClientView[]; next?: string }> => {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([k, v]) => { if (v && v.trim()) params[k] = v.trim(); });
  if (cursor) params.cursor = cursor;
  return (await gatewayApi.get<{ profiles: BaClientView[]; next?: string }>(CATALOGUE, { params })).data;
};

export const getBaCatalogueProfile = async (profileId: string): Promise<BaClientView> =>
  (await gatewayApi.get<BaClientView>(`${CATALOGUE}/${profileId}`)).data;
